import React, { useState } from 'react';
import { DeleteConfirmModal, SKIP_DELETE_CONFIRM_KEY } from '../components/DeleteConfirmModal';

interface PendingDelete {
  title: string;
  description: string;
  callback: () => void;
}

/**
 * Hook that provides a consistent delete-confirmation flow across the app.
 *
 * Usage:
 *   const { requestDelete, modal } = useDeleteConfirm();
 *
 *   // Trigger a delete with confirmation
 *   requestDelete('Remove Medication', 'This cannot be undone.', () => deleteMedication(id));
 *
 *   // Render the modal somewhere in the component's JSX
 *   return <div>...{modal}</div>;
 *
 * If the user has previously checked "Don't show again", `requestDelete` will
 * execute the callback immediately without showing the modal.
 * The preference is stored under the key `koda_skip_delete_confirm` in
 * localStorage and is shared across the whole app.
 */
export function useDeleteConfirm() {
  const [pending, setPending] = useState<PendingDelete | null>(null);

  const requestDelete = (title: string, description: string, callback: () => void) => {
    const skip = localStorage.getItem(SKIP_DELETE_CONFIRM_KEY) === 'true';
    if (skip) {
      callback();
    } else {
      setPending({ title, description, callback });
    }
  };

  const handleConfirm = () => {
    pending?.callback();
    setPending(null);
  };

  const handleCancel = () => {
    setPending(null);
  };

  const modal = (
    <DeleteConfirmModal
      isOpen={pending !== null}
      title={pending?.title}
      description={pending?.description}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { requestDelete, modal };
}
